// Document Template Designs for Merai
// Templates: Classic, Modern, Corporate, Compact, Elegant

export const DOCUMENT_TEMPLATES = {
  classic: {
    id: 'classic',
    name: 'Classic',
    description: 'Clean, professional design suitable for all businesses',
    preview: '/templates/classic.png',
    styles: {
      headerBg: '#ffffff',
      headerText: '#131A17',
      accentColor: '#0B6B4F',
      bodyBg: '#ffffff',
      tableBorder: '#E5E2D9',
      tableHeaderBg: '#F7F6F2',
      footerBg: '#F7F6F2',
    }
  },
  modern: {
    id: 'modern',
    name: 'Modern',
    description: 'Contemporary design with bold colors',
    preview: '/templates/modern.png',
    styles: {
      headerBg: '#0C1F1A',
      headerText: '#ffffff',
      accentColor: '#0B6B4F',
      bodyBg: '#ffffff',
      tableBorder: '#E5E2D9',
      tableHeaderBg: '#0C1F1A',
      tableHeaderText: '#ffffff',
      footerBg: '#F7F6F2',
    }
  },
  corporate: {
    id: 'corporate',
    name: 'Corporate',
    description: 'Formal, structured layout for enterprise',
    preview: '/templates/corporate.png',
    styles: {
      headerBg: '#1e293b',
      headerText: '#ffffff',
      accentColor: '#3b82f6',
      bodyBg: '#ffffff',
      tableBorder: '#cbd5e1',
      tableHeaderBg: '#1e293b',
      tableHeaderText: '#ffffff',
      footerBg: '#f1f5f9',
    }
  },
  compact: {
    id: 'compact',
    name: 'Compact',
    description: 'Single page, condensed layout',
    preview: '/templates/compact.png',
    styles: {
      headerBg: '#ffffff',
      headerText: '#374151',
      accentColor: '#6366f1',
      bodyBg: '#ffffff',
      tableBorder: '#e5e7eb',
      tableHeaderBg: '#f3f4f6',
      footerBg: '#ffffff',
      fontSize: 'small',
    }
  },
  elegant: {
    id: 'elegant',
    name: 'Elegant',
    description: 'Stylish design for premium services',
    preview: '/templates/elegant.png',
    styles: {
      headerBg: 'linear-gradient(135deg, #0C1F1A 0%, #0B6B4F 100%)',
      headerText: '#ffffff',
      accentColor: '#d4af37',
      bodyBg: '#ffffff',
      tableBorder: '#d1d5db',
      tableHeaderBg: '#faf5e6',
      tableHeaderText: '#78716c',
      footerBg: '#faf5e6',
    }
  }
};

// Generate HTML template based on template type and document data
export const getCompanyDocumentTemplate = (company, docType) => {
  const fields = {
    invoice: 'default_template_invoice',
    quotation: 'default_template_quotation',
    sales_order: 'default_template_sales_order',
    delivery_challan: 'default_template_delivery_challan',
    payment_receipt: 'default_template_payment_receipt',
    credit_note: 'default_template_credit_note',
    estimate: 'default_template_estimate',
  };
  const value = company?.[fields[docType]] || company?.default_document_template;
  return DOCUMENT_TEMPLATES[value] ? value : 'classic';
};

export const resolveDocumentTemplate = (doc, company, docType) => {
  const recordTemplate = doc && doc.template_id;
  if (recordTemplate && DOCUMENT_TEMPLATES[recordTemplate]) {
    return recordTemplate;
  }
  return getCompanyDocumentTemplate(company, docType);
};
export const generateDocumentHTML = (templateId, docType, data, company) => {
  templateId = getCompanyDocumentTemplate(company, docType);
  const template = DOCUMENT_TEMPLATES[templateId] || DOCUMENT_TEMPLATES.classic;
  const styles = template.styles;
  
  const docTypeLabels = {
    quotation: 'QUOTATION',
    invoice: 'INVOICE',
    sales_order: 'SALES ORDER',
    purchase_order: 'PURCHASE ORDER',
    delivery_challan: 'DELIVERY CHALLAN',
    payment_receipt: 'PAYMENT RECEIPT',
    credit_note: 'CREDIT NOTE',
    estimate: 'ESTIMATE',
    bill: 'BILL',
  };

  const docLabel = docTypeLabels[docType] || docType.toUpperCase();
  
  // Format currency
  const formatCurrency = (amount) => {
    const currency = company?.currency || 'USD';
    const symbols = { USD: '$', EUR: '€', GBP: '£', AED: 'د.إ', CNY: '¥', INR: '₹' };
    return `${symbols[currency] || '$'}${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr));
    const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric' 
    });
  };

  // Generate items table rows
  const itemsHTML = (data.items || []).map((item, index) => `
    <tr style="border-bottom: 1px solid ${styles.tableBorder};">
      <td style="padding: 12px; text-align: center;">${index + 1}</td>
      <td style="padding: 12px;">${item.product || item.description || item.name || '-'}</td>
      <td style="padding: 12px; text-align: center;">${item.quantity || 1}</td>
      <td style="padding: 12px; text-align: right;">${formatCurrency(item.price || item.unit_price || 0)}</td>
      <td style="padding: 12px; text-align: right; font-weight: 500;">${formatCurrency((item.quantity || 1) * (item.price || item.unit_price || 0))}</td>
    </tr>
  `).join('');

  // Template-specific header styles
  const getHeaderStyle = () => {
    if (templateId === 'elegant') {
      return `background: ${styles.headerBg}; color: ${styles.headerText}; padding: 40px;`;
    }
    if (templateId === 'modern' || templateId === 'corporate') {
      return `background: ${styles.headerBg}; color: ${styles.headerText}; padding: 30px;`;
    }
    return `background: ${styles.headerBg}; color: ${styles.headerText}; padding: 30px; border-bottom: 3px solid ${styles.accentColor};`;
  };

  // Template-specific table header styles
  const getTableHeaderStyle = () => {
    if (styles.tableHeaderText) {
      return `background: ${styles.tableHeaderBg}; color: ${styles.tableHeaderText};`;
    }
    return `background: ${styles.tableHeaderBg}; color: #5C6660;`;
  };

  const logoHTML = company?.logo_url ? '<img src="' + company.logo_url + '" alt="Company logo" style="max-height: 56px; max-width: 180px; object-fit: contain; margin-bottom: 8px; display: block;" />' : '';
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${docLabel} - ${data.id || 'Document'}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
          font-family: 'Inter', -apple-system, sans-serif; 
          font-size: ${styles.fontSize === 'small' ? '12px' : '14px'}; 
          line-height: 1.5; 
          color: #131A17;
          background: #fff;
        }
        .document { max-width: 800px; margin: 0 auto; background: ${styles.bodyBg}; }
        .header { ${getHeaderStyle()} }
        .header-content { display: flex; justify-content: space-between; align-items: flex-start; }
        .company-info h1 { font-size: 24px; font-weight: 700; margin-bottom: 4px; }
        .company-info p { opacity: 0.9; font-size: 13px; }
        .doc-info { text-align: right; }
        .doc-type { font-size: 28px; font-weight: 700; letter-spacing: 2px; }
        .doc-number { font-size: 14px; opacity: 0.9; margin-top: 4px; }
        .doc-date { font-size: 13px; opacity: 0.8; margin-top: 8px; }
        .parties { display: flex; justify-content: space-between; padding: 30px; gap: 40px; }
        .party { flex: 1; }
        .party-label { font-size: 11px; font-weight: 600; color: ${styles.accentColor}; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px; }
        .party-name { font-size: 16px; font-weight: 600; color: #131A17; margin-bottom: 4px; }
        .party-details { font-size: 13px; color: #5C6660; }
        .items-section { padding: 0 30px 30px; }
        .items-table { width: 100%; border-collapse: collapse; }
        .items-table th { ${getTableHeaderStyle()} padding: 12px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
        .items-table td { padding: 12px; }
        .totals { display: flex; justify-content: flex-end; padding: 0 30px 30px; }
        .totals-box { width: 280px; }
        .total-row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 13px; }
        .total-row.subtotal { border-bottom: 1px solid ${styles.tableBorder}; }
        .total-row.grand-total { font-size: 18px; font-weight: 700; color: ${styles.accentColor}; padding-top: 12px; border-top: 2px solid ${styles.accentColor}; margin-top: 8px; }
        .footer { background: ${styles.footerBg}; padding: 25px 30px; }
        .footer-content { display: flex; justify-content: space-between; gap: 40px; }
        .footer-section h4 { font-size: 11px; font-weight: 600; color: ${styles.accentColor}; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px; }
        .footer-section p { font-size: 12px; color: #5C6660; }
        .notes { padding: 20px 30px; background: #fffbeb; border-left: 4px solid #f59e0b; margin: 0 30px 30px; }
        .notes-label { font-size: 11px; font-weight: 600; color: #b45309; margin-bottom: 4px; }
        .notes-text { font-size: 13px; color: #78716c; }
        @media print {
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          .document { box-shadow: none; }
        }
      </style>
    </head>
    <body>
      <div class="document">
        <!-- Header -->
        <div class="header">
          <div class="header-content">
            <div class="company-info">
              $${logoHTML}
          <h1>$${company?.name || 'Your Company'}</h1>
              <p>${company?.address || ''}</p>
              <p>${company?.email || ''} ${company?.phone ? '• ' + company.phone : ''}</p>
              ${company?.tax_id ? `<p>Tax ID: ${company.tax_id}</p>` : ''}
            </div>
            <div class="doc-info">
              <div class="doc-type">${docLabel}</div>
              <div class="doc-number">${data.document_number || ('#' + (data.id?.slice(0, 8).toUpperCase() || '00000000'))}</div>
              <div class="doc-date">
                Date: ${formatDate(data.invoice_date || data.created_at || data.date)}<br>
                ${data.due_date ? `Due: ${formatDate(data.due_date)}` : ''}
                ${data.valid_until ? `Valid Until: ${formatDate(data.valid_until)}` : ''}
              </div>
            </div>
          </div>
        </div>

        <!-- Parties -->
        <div class="parties">
          <div class="party">
            <div class="party-label">${docType === 'purchase_order' || docType === 'bill' ? 'Supplier' : 'Bill To'}</div>
            <div class="party-name">${data.client_name || data.supplier_name || '-'}</div>
            <div class="party-details">
              ${data.client_email || data.supplier_email || ''}<br>
              ${data.client_phone || data.supplier_phone || ''}<br>
              ${data.client_address || data.supplier_address || ''}
            </div>
          </div>
          <div class="party">
            <div class="party-label">From</div>
            <div class="party-name">${company?.name || 'Your Company'}</div>
            <div class="party-details">
              ${company?.address || ''}<br>
              ${company?.email || ''}<br>
              ${company?.phone || ''}
            </div>
          </div>
        </div>

        <!-- Items Table -->
        <div class="items-section">
          <table class="items-table">
            <thead>
              <tr>
                <th style="width: 50px; text-align: center;">#</th>
                <th style="text-align: left;">Description</th>
                <th style="width: 80px; text-align: center;">Qty</th>
                <th style="width: 120px; text-align: right;">Unit Price</th>
                <th style="width: 120px; text-align: right;">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHTML || '<tr><td colspan="5" style="text-align: center; padding: 20px; color: #8A948E;">No items</td></tr>'}
            </tbody>
          </table>
        </div>

        <!-- Totals -->
        <div class="totals">
          <div class="totals-box">
            <div class="total-row subtotal">
              <span>Subtotal</span>
              <span>${formatCurrency(data.subtotal || 0)}</span>
            </div>
            ${data.discount ? `
            <div class="total-row">
              <span>Discount</span>
              <span>-${formatCurrency(data.discount_amount ?? (data.discount_type === 'percent' ? (data.subtotal || 0) * data.discount / 100 : data.discount))}</span>
            </div>
            ` : ''}
            <div class="total-row">
              <span>Tax ${data.tax_rate ? `(${data.tax_rate}%)` : ''}</span>
              <span>${formatCurrency(data.tax || 0)}</span>
            </div>
            <div class="total-row grand-total">
              <span>Total</span>
              <span>${formatCurrency(data.total || 0)}</span>
            </div>
          </div>
        </div>

        <!-- Notes -->
        ${data.notes ? `
        <div class="notes">
          <div class="notes-label">Notes</div>
          <div class="notes-text">${data.notes}</div>
        </div>
        ` : ''}

        <!-- Footer -->
        <div class="footer">
          <div class="footer-content">
            <div class="footer-section">
              <h4>Payment Terms</h4>
              <p>${data.payment_terms || 'Net 30 days'}</p>
            </div>
            <div class="footer-section">
              <h4>Bank Details</h4>
              <p>Bank: ${company?.bank_name || 'Your Bank'}<br>
              Account: ${company?.bank_account || 'XXXX-XXXX-XXXX'}</p>
            </div>
            <div class="footer-section">
              <h4>Contact</h4>
              <p>${company?.email || 'contact@company.com'}<br>
              ${company?.phone || ''}</p>
            </div>
          </div>
        </div>
      </div>
    </body>
    </html>
  `;

  return html;
};

export default DOCUMENT_TEMPLATES;
